import SentryService from '@services/SentryService';
import type { DBSchema, IDBPDatabase } from 'idb';
import { openDB } from 'idb';

import type { IStorageBackend } from './types';

const DB_NAME = 'JotPsychChunkStorage';
const DB_VERSION = 1;
const STORE_NAME = 'chunks' as const;

interface ChunkDB extends DBSchema {
  chunks: {
    key: string; // "sessionId/chunkIndex:06d"
    value: ArrayBuffer;
  };
}

/** Zero-pad a chunk index to 6 digits for lexicographic ordering. */
function chunkKey(sessionId: string, chunkIndex: number): string {
  return `${sessionId}/${String(chunkIndex).padStart(6, '0')}`;
}

/**
 * IndexedDB-backed implementation of IStorageBackend.
 *
 * Uses a dedicated database (`JotPsychChunkStorage`) separate from the
 * existing `SmartScribeAudioChunks` database. Chunks are keyed as
 * `"sessionId/000042"` — simple string keys that support IDBKeyRange
 * prefix queries for per-session operations.
 */
export class IndexedDBStorageBackend implements IStorageBackend {
  private db: IDBPDatabase<ChunkDB> | null = null;

  async initialize(): Promise<void> {
    SentryService.addBreadcrumb({
      category: 'storage.indexeddb',
      message: `Opening ${DB_NAME} v${DB_VERSION}`,
      level: 'info',
    });

    this.db = await openDB<ChunkDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }

  async writeChunk(sessionId: string, chunkIndex: number, data: ArrayBuffer): Promise<void> {
    const db = this.ensureOpen();
    const key = chunkKey(sessionId, chunkIndex);
    const span = SentryService.startInactiveSpan('storage.indexeddb.write', key);

    try {
      const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
      await tx.store.put(data, key);
      await tx.done;
      span?.end();
    } catch (error) {
      span?.end();
      this.handleWriteError(error, key);
      throw error;
    }
  }

  async readChunk(sessionId: string, chunkIndex: number): Promise<ArrayBuffer | null> {
    const db = this.ensureOpen();
    const key = chunkKey(sessionId, chunkIndex);

    return this.withRetry(db, 'readonly', async () => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const result = await tx.store.get(key);
      await tx.done;
      return result ?? null;
    });
  }

  async hasChunk(sessionId: string, chunkIndex: number): Promise<boolean> {
    const db = this.ensureOpen();
    const key = chunkKey(sessionId, chunkIndex);

    return this.withRetry(db, 'readonly', async () => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const foundKey = await tx.store.getKey(key);
      await tx.done;
      return foundKey !== undefined;
    });
  }

  async deleteChunk(sessionId: string, chunkIndex: number): Promise<void> {
    const db = this.ensureOpen();
    const key = chunkKey(sessionId, chunkIndex);

    await this.withRetry(db, 'readwrite', async () => {
      const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
      await tx.store.delete(key);
      await tx.done;
    });
  }

  async listChunks(sessionId: string): Promise<number[]> {
    const db = this.ensureOpen();
    const prefix = `${sessionId}/`;
    const range = IDBKeyRange.bound(prefix, prefix + '\uffff');

    const keys = await this.withRetry(db, 'readonly', async () => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const allKeys = await tx.store.getAllKeys(range);
      await tx.done;
      return allKeys;
    });

    return keys
      .map((k: string) => parseInt(k.split('/')[1], 10))
      .sort((a: number, b: number) => a - b);
  }

  async deleteSession(sessionId: string): Promise<void> {
    const db = this.ensureOpen();
    const prefix = `${sessionId}/`;
    const range = IDBKeyRange.bound(prefix, prefix + '\uffff');

    await this.withRetry(db, 'readwrite', async () => {
      const tx = db.transaction(STORE_NAME, 'readwrite', { durability: 'relaxed' });
      let cursor = await tx.store.openCursor(range);
      while (cursor) {
        cursor.delete();
        cursor = await cursor.continue();
      }
      await tx.done;
    });

    SentryService.addBreadcrumb({
      category: 'storage.indexeddb',
      message: `Deleted session ${sessionId}`,
      level: 'info',
    });
  }

  async listSessions(): Promise<string[]> {
    const db = this.ensureOpen();

    const keys = await this.withRetry(db, 'readonly', async () => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const allKeys = await tx.store.getAllKeys();
      await tx.done;
      return allKeys;
    });

    // Extract unique sessionId prefixes from compound keys "sessionId/chunkIndex"
    const sessionIds = new Set<string>();
    for (const key of keys) {
      const slashIndex = key.lastIndexOf('/');
      if (slashIndex > 0) {
        sessionIds.add(key.substring(0, slashIndex));
      }
    }

    return Array.from(sessionIds);
  }

  /** Close the database connection. */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // ── Private helpers ──

  private ensureOpen(): IDBPDatabase<ChunkDB> {
    if (!this.db) {
      throw new Error('IndexedDBStorageBackend not initialized — call initialize() first');
    }
    return this.db;
  }

  /**
   * Execute a callback with retry on transient IndexedDB errors.
   * Mirrors the safeTransaction pattern from IndexDBService.ts.
   */
  private async withRetry<T>(
    _db: IDBPDatabase<ChunkDB>,
    _mode: 'readonly' | 'readwrite',
    fn: () => Promise<T>,
    retries = 1,
  ): Promise<T> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        const isRetryable =
          error instanceof DOMException &&
          (error.name === 'InvalidStateError' ||
            error.name === 'TransactionInactiveError' ||
            error.message.includes('database connection is closing'));

        if (isRetryable && attempt < retries) {
          SentryService.addBreadcrumb({
            category: 'storage.indexeddb',
            message: `Transaction retry (attempt ${attempt + 1})`,
            level: 'warning',
            data: { error: error instanceof DOMException ? error.name : 'unknown' },
          });
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
          continue;
        }

        throw error;
      }
    }

    throw new Error('withRetry: unreachable');
  }

  private handleWriteError(error: unknown, key: string): void {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      SentryService.captureException(new Error(`Storage quota exceeded writing ${key}`));
    }
  }
}
